/**
 * Translates TC v4 backend (final-JSON) validation errors into user-friendly
 * messages. The backend reports failures as {field, message} where `field` is
 * the raw JSON property path, e.g.
 *   tcProductInformation[0].incomingTcProductInformation[0]
 *     .incomingTcProductNetWeightUsedInformation[0]
 *     .tcProductIncomingRawMaterialInformation[0]
 *     .tcProductIncomingCertifiedRawMaterialWeightUsed
 * Those internal paths must never reach end users (ClickUp 86ajcn0z8); this
 * module maps every path segment to its UI label and rewrites the message.
 */

export interface TcValidationDetail {
  field: string;
  message: string;
}

// Array/object container segments -> UI section label (index appended as 1-based).
const CONTAINER_LABELS: Record<string, string> = {
  tcProductInformation: "Product",
  incomingTcProductInformation: "Incoming TC",
  incomingTcProductNetWeightUsedInformation: "Incoming Product Article",
  tcProductIncomingRawMaterialInformation: "Incoming Raw Material",
  tcProductComponentInformation: "Component",
  tcProductComponentCertifiedWeightInformation: "Component Claims Category Weight",
  tcProductComponentRawMaterialInformation: "Component Raw Material",
  tcProductCertifiedWeightInformation: "Product Claims Category Weight",
  tcProductRawMaterialInformation: "Product Raw Material",
  tcProductCertifiedRawMaterialGeographicInformation: "Certified Raw Material Origin",
  tcProductCertifiedRawMaterialWeightInformation: "Certified Raw Material Weight",
  tcClaimsCategoryCertifiedWeightInformation: "Claims Category Certified Weight",
  tcStandardCertifiedWeightInformation: "Standard Certified Weight",
  tcShipmentInformation: "Shipment",
  tcProductFacilitiesInformation: "Product Facility",
  tcProductFacilityInformation: "Product Facility",
  tcProductFirstProcessorInformation: "First Processor",
  tcSellerInformation: "Seller Information",
  tcBuyerInformation: "Buyer Information",
  tcSellerNonCertifiedTraderInformation: "Seller Non-Certified Trader",
};

// Leaf JSON fields -> UI field label. Covers every field the TC v4 final-JSON
// validator can report (keep in sync with shared_v4/json_validation/tc_v4_final_json.py).
const FIELD_LABELS: Record<string, string> = {
  // TC base
  tcNo: "TC Number",
  tcVersionNo: "TC Version Number",
  tcStatus: "TC Status",
  tcCbLicensingCode: "CB Licensing Code",
  tcStandard: "TC Standard",
  tcIssueDate: "Issue Date",
  tcPlaceOfIssue: "Place of Issue",
  tcApplicationReceivedDate: "Application Received Date",
  tcApplicationCompleteDate: "Application Complete Date",
  tcLastUpdatedDate: "Last Updated Date",
  tcAmendmentReason: "Amendment Reason",
  tcInvalidationReason: "Invalidation Reason",
  tcDelayReason: "Delay Reason",
  tcIsReturn: "Return TC Flag",
  tcUsdaNopCompliantFlag: "USDA NOP Compliant Flag",
  tcNetShippingWeight: "Net Shipping Weight",
  tcCertifiedWeight: "Certified Weight",
  tcSellerProductDeclarations: "Seller Product Declarations",
  // Seller / buyer
  sellerTeId: "Seller TE-ID",
  sellerName: "Seller Name",
  sellerStreetAddress: "Seller Street Address",
  sellerTown: "Seller Town",
  sellerPostcode: "Seller Postcode",
  sellerStateOrProvince: "Seller State/Province",
  sellerCountryOrArea: "Seller Country/Area",
  sellerCertifiedOrganizationName: "Seller Certified Organization Name",
  sellerCbClientNo: "Seller CB Client Number",
  sellerScNo: "Seller SC Number",
  sellerNonCertifiedTraderName: "Seller Non-Certified Trader Name",
  sellerNonCertifiedTraderTeId: "Seller Non-Certified Trader TE-ID",
  buyerTeId: "Buyer TE-ID",
  buyerName: "Buyer Name",
  buyerStreetAddress: "Buyer Street Address",
  buyerTown: "Buyer Town",
  buyerPostcode: "Buyer Postcode",
  buyerStateOrProvince: "Buyer State/Province",
  buyerCountryOrArea: "Buyer Country/Area",
  buyerCertifiedOrganizationName: "Buyer Certified Organization Name",
  buyerCbClientNo: "Buyer CB Client Number",
  buyerScCbLicensingCode: "Buyer CB Licensing Code",
  buyerScNo: "Buyer SC Number",
  // Claims category (TC level)
  tcClaimsCategory: "Claims Category",
  tcClaimsCategoryCertifiedWeight: "Claims Category Certified Weight",
  // Shipments
  tcShipmentDocNo: "Shipment Document Number",
  tcShipmentDate: "Shipment Date",
  tcShipmentInvoiceReferences: "Shipment Invoice References",
  tcShipmentGrossShippingWeight: "Gross Shipping Weight",
  tcShipToName: "Ship-To Name",
  tcShipToTeId: "Ship-To TE-ID",
  tcShipToStreetAddress: "Ship-To Street Address",
  tcShipToTown: "Ship-To Town",
  tcShipToPostcode: "Ship-To Postcode",
  tcShipToStateOrProvince: "Ship-To State/Province",
  tcShipToCountryOrArea: "Ship-To Country/Area",
  // Product
  tcProductArticleNo: "Product Article Number",
  tcProductOrderNo: "Product Order Number",
  tcProductGtin: "Product GTIN",
  tcProductShipmentDocNo: "Product Shipment Document Number",
  tcProductShipmentNo: "Product Shipment Number",
  tcProductNumberOfUnits: "Number of Units",
  tcProductUnitsMetric: "Product Units Metric",
  tcProductUnitsMetricOther: "Product Units Metric (Other)",
  tcProductCategoryCode: "Product Category",
  tcProductCategoryUserSpecificTerm: "Product Category User-Specific Term",
  tcProductDetailCode: "Product Detail",
  tcProductDetailUserSpecificTerm: "Product Detail User-Specific Term",
  tcProductNetShippingWeight: "Product Net Shipping Weight",
  tcProductSupplementaryWeight: "Product Supplementary Weight",
  tcProductAdditives: "Product Additives",
  tcProductAdditivesWeight: "Product Additives Weight",
  tcProductCertifiedWeight: "Product Certified Weight",
  tcProductProductionDate: "Production Date",
  tcProductOriginFacilityTeId: "Origin Facility TE-ID",
  tcProductOriginOrganizationTeId: "Origin Organization TE-ID",
  tcProductFeedstock: "Product Feedstock",
  tcProductReclaimedMaterialSource: "Reclaimed Material Source",
  tcProductPercentTextileWaste: "Percent Textile Waste",
  tcProductPackagingFor: "Packaging For",
  tcProductFirstProcessCode: "First Process Code",
  tcProductFirstProcessorTeId: "First Processor TE-ID",
  tcProductFacilitiesProcessCode: "Facility Process Code",
  tcProductFacilitiesTeId: "Facility TE-ID",
  tcProductFacilityProcessCode: "Facility Process Code",
  tcProductFacilityTeId: "Facility TE-ID",
  tcProductClaimsCategory: "Product Claims Category",
  tcProductClaimsCategoryCertifiedWeight: "Product Claims Category Certified Weight",
  tcProductInfoYarnCount: "Yarn Count",
  tcProductInfoFiberLength: "Fiber Length",
  tcProductInfoFabricWeightAndConstruct: "Fabric Weight and Construction",
  tcProductInfoFabricWeightOfFinishedProduct: "Fabric Weight of Finished Product",
  tcProductInfoTechnicalSpecifications: "Technical Specifications",
  tcProductInfoSupplementaryWeight: "Supplementary Weight Info",
  tcProductInfoAdditional: "Additional Product Info",
  // Product raw materials
  tcProductRawMaterialCode: "Raw Material",
  tcProductRawMaterialPercentage: "Raw Material Percentage",
  tcProductRawMaterialUserSpecificTerm: "Raw Material User-Specific Term",
  tcProductCertifiedRawMaterialCode: "Certified Raw Material",
  tcProductCertifiedRawMaterialWeight: "Certified Raw Material Weight",
  tcProductCertifiedRawMaterialCountryOrArea: "Certified Raw Material Country/Area",
  tcProductCertifiedRawMaterialStateOrProvince: "Certified Raw Material State/Province",
  // Components
  tcProductComponentNo: "Component Number",
  tcProductComponentDetail: "Component Detail",
  tcProductComponentNetShippingWeight: "Component Net Shipping Weight",
  tcProductComponentClaimsCategory: "Component Claims Category",
  tcProductComponentClaimsCategoryCertifiedWeight: "Component Claims Category Certified Weight",
  tcProductComponentCertifiedWeight: "Component Certified Weight",
  tcProductComponentRawMaterialCode: "Component Raw Material",
  tcProductComponentRawMaterialPercentage: "Component Raw Material Percentage",
  tcProductComponentInfoYarnCount: "Component Yarn Count",
  tcProductComponentInfoFiberLength: "Component Fiber Length",
  tcProductComponentInfoFabricWeightAndConstruct: "Component Fabric Weight and Construction",
  tcProductComponentInfoFabricWeightOfFinishedProduct: "Component Fabric Weight of Finished Product",
  tcProductComponentInfoTechnicalSpecifications: "Component Technical Specifications",
  tcProductComponentInfoAdditional: "Additional Component Info",
  // Incoming TC
  incomingTcLicensingCode: "Incoming TC Licensing Code",
  tcProductIncomingTcNo: "Incoming TC Number",
  tcProductIncomingTcCbLicensingCode: "Incoming TC CB Licensing Code",
  tcProductIncomingTcV4Issued: "Incoming TC v4 Issued Flag",
  tcProductIncomingTcStandard: "Incoming TC Standard",
  tcProductIncomingArticleNo: "Incoming Product Article Number",
  tcProductIncomingArticleNetWeightUsed: "Incoming Article Net Weight Used",
  tcProductIncomingProductCategoryCode: "Incoming Product Category",
  tcProductIncomingProductDetailCode: "Incoming Product Detail",
  tcProductIncomingCertifiedRawMaterialCode: "Incoming Certified Raw Material",
  tcProductIncomingCertifiedRawMaterialWeightUsed: "Incoming Certified Raw Material Weight Used",
};

// Container names that can also appear as the leaf of an error path (e.g.
// "At least one raw material row is required" on tcProductRawMaterialInformation).
const SECTION_AS_LEAF_LABELS: Record<string, string> = {
  ...CONTAINER_LABELS,
  tcProductInformation: "Products Information",
  tcShipmentInformation: "Shipment Information",
  incomingTcProductInformation: "Incoming TC Information",
  tcProductRawMaterialInformation: "Product Raw Material Information",
  tcProductComponentInformation: "Component Information",
};

interface PathSegment {
  name: string;
  index?: number;
}

const parseFieldPath = (field: string): PathSegment[] => {
  const segments: PathSegment[] = [];
  for (const part of String(field || "").split(".")) {
    if (!part) continue;
    const match = /^([^[\]]+)((?:\[\d+\])*)$/.exec(part);
    if (!match) {
      segments.push({ name: part });
      continue;
    }
    const [, name, indexPart] = match;
    const indices = indexPart ? indexPart.match(/\d+/g) : null;
    if (indices && indices.length > 0) {
      // e.g. tcProductFeedstock[2] or nested [i][j] (first index wins for context)
      segments.push({ name, index: Number(indices[0]) });
    } else {
      segments.push({ name });
    }
  }
  return segments;
};

// Fallback for unmapped fields: camelCase -> Title Case, dropping the tc prefix.
const humanizeFieldName = (name: string): string => {
  const withoutPrefix = name.replace(/^tc(?=[A-Z])/, "");
  const spaced = withoutPrefix
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const leafLabel = (name: string): string =>
  FIELD_LABELS[name] || SECTION_AS_LEAF_LABELS[name] || humanizeFieldName(name);

/**
 * Rewrites the backend message around the friendly field label. Backend
 * messages usually restate the raw field in sentence case ("Incoming certified
 * raw material weight used must be greater than 0"), so when the message
 * starts with the label text we re-case it; otherwise generic messages
 * ("Value is required") are rephrased against the label.
 */
const rewriteMessage = (label: string, message: string): string => {
  const trimmed = String(message || "").trim();
  if (!trimmed) return `${label} is invalid`;

  const lowerMsg = trimmed.toLowerCase();
  const lowerLabel = label.toLowerCase();
  if (lowerMsg.startsWith(lowerLabel)) {
    return `${label}${trimmed.slice(label.length)}`;
  }

  const genericRewrites: Array<[RegExp, (m: RegExpExecArray) => string]> = [
    [/^value is required$/i, () => `${label} is required`],
    [/^value must be a string$/i, () => `${label} must be text`],
    [/^valid numeric value is required$/i, () => `${label} must be a valid number`],
    [/^valid numeric weight is required$/i, () => `${label} must be a valid weight`],
    [/^at least one value is required$/i, () => `At least one ${label} value is required`],
    [/^disallowed placeholder value$/i, () => `${label} has an invalid placeholder value`],
    [/^maximum length is (\d+)$/i, (m) => `${label} must be at most ${m[1]} characters`],
    [/^maximum decimal places is (\d+)$/i, (m) => `${label} allows at most ${m[1]} decimal places`],
    [/^weight must be non-negative$/i, () => `${label} must be non-negative`],
    [/^percentage must be/i, () => `${label}: ${trimmed}`],
    [/^expected (.+)$/i, (m) => `${label} is invalid — expected ${m[1]}`],
    [/^must be (.+)$/i, (m) => `${label} must be ${m[1]}`],
    [/^allowed values are (.+)$/i, (m) => `${label} allows only: ${m[1]}`],
    [/^invalid (.+)$/i, () => `${label}: ${trimmed}`],
    [/^duplicate (.+)$/i, () => trimmed],
    [/^required when (.+)$/i, (m) => `${label} is required when ${m[1]}`],
  ];
  for (const [pattern, build] of genericRewrites) {
    const match = pattern.exec(trimmed);
    if (match) return build(match);
  }
  return `${label}: ${trimmed}`;
};

/**
 * Translates a single backend validation detail into a user-friendly message,
 * prefixed with its location context (1-based indices), e.g.
 * "Product 1 › Incoming TC 1 › Incoming Raw Material 1: Incoming Certified
 *  Raw Material Weight Used must be greater than 0".
 */
export const translateTcV4ValidationDetail = (detail: TcValidationDetail): string => {
  const segments = parseFieldPath(detail.field);
  if (segments.length === 0) {
    return String(detail.message || "Validation error");
  }

  const leaf = segments[segments.length - 1];
  const containers = segments.slice(0, -1);

  const contextParts = containers
    .map((segment) => {
      const label = CONTAINER_LABELS[segment.name] || humanizeFieldName(segment.name);
      return segment.index !== undefined ? `${label} ${segment.index + 1}` : label;
    })
    // Skip redundant unindexed wrappers to keep the prefix short.
    .filter((part, idx, arr) => arr.indexOf(part) === idx);

  const label = leafLabel(leaf.name);
  const message = rewriteMessage(label, detail.message);
  return contextParts.length > 0 ? `${contextParts.join(" › ")}: ${message}` : message;
};

export const translateTcV4ValidationDetails = (
  details: TcValidationDetail[]
): string[] => (details || []).map(translateTcV4ValidationDetail);

/**
 * Maps a raw backend/internal field name (JSON property or pydantic loc tail)
 * to its UI label — for surfacing per-field API errors outside the final-JSON
 * submit flow (e.g. 422 model validation on tab saves).
 */
export const translateBackendFieldName = (name: string): string =>
  leafLabel(String(name || "").trim() || "Field");

/**
 * Friendly replacement for backend top-level error strings that would
 * otherwise leak internals (e.g. "Final TC v4 JSON validation failed").
 */
export const friendlyTcV4SubmitError = (error: string): string => {
  const normalized = String(error || "").trim();
  if (/final tc v4 json validation failed/i.test(normalized)) {
    return "The TC could not be submitted because some fields failed validation. Please review the messages below and correct the highlighted fields.";
  }
  return normalized;
};
